import { useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import CustomerAutocomplete from "./CustomerAutocomplete";
import ItemAutocomplete from "./ItemAutocomplete";
import { Mail, FileText, Image as ImageIcon, Trash2, Loader2, AlertTriangle, CheckCircle2, Sparkles } from "lucide-react";

const MODES = [
  { key: "email", label: "E-Mail / Text", icon: Mail },
  { key: "pdf", label: "PDF", icon: FileText },
  { key: "image", label: "Bild / Scan", icon: ImageIcon },
];

export default function OrderImportDialog({ open, onClose, customers = [], items = [], onCreated }) {
  const [mode, setMode] = useState("email");
  const [text, setText] = useState("");
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(null); // matched result (editable)

  const reset = () => { setMode("email"); setText(""); setFile(null); setPreview(null); setLoading(false); setSaving(false); };
  const close = () => { reset(); onClose?.(); };

  const analyze = async () => {
    const fd = new FormData();
    fd.append("mode", mode);
    if (mode === "email") {
      if (!text.trim() && !file) { toast.error("Bitte Text einfügen oder .eml-Datei wählen"); return; }
      if (text.trim()) fd.append("text", text);
      if (file) fd.append("file", file);
    } else {
      if (!file) { toast.error("Bitte eine Datei auswählen"); return; }
      fd.append("file", file);
    }
    setLoading(true);
    try {
      const { data } = await api.post("/orders/import/preview", fd);
      setPreview({
        ...data,
        lines: (data.lines || []).map((l) => ({ ...l, price: "" })),
      });
      toast.success("Bestellung erkannt – bitte prüfen");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erkennung fehlgeschlagen");
    } finally { setLoading(false); }
  };

  const setLine = (idx, patch) => setPreview((p) => ({ ...p, lines: p.lines.map((l, i) => (i === idx ? { ...l, ...patch } : l)) }));
  const removeLine = (idx) => setPreview((p) => ({ ...p, lines: p.lines.filter((_, i) => i !== idx) }));

  const save = async () => {
    if (!preview?.customer_id) { toast.error("Bitte einen Kunden zuordnen"); return; }
    const mapped = preview.lines.filter((l) => l.item_id && Number(l.quantity) > 0);
    if (!mapped.length) { toast.error("Mindestens eine zugeordnete Position mit Menge nötig"); return; }
    setSaving(true);
    try {
      const payload = {
        customer_id: preview.customer_id,
        expected_delivery: preview.expected_delivery || null,
        notes: preview.notes || null,
        lines: mapped.map((l) => ({ item_id: l.item_id, quantity: Number(l.quantity), price: Number(l.price || 0) })),
      };
      const { data } = await api.post("/orders/import/commit", payload);
      toast.success(`Bestellung ${data.number} importiert`);
      close();
      onCreated?.(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally { setSaving(false); }
  };

  const unmatchedCount = preview?.lines?.filter((l) => !l.item_id).length || 0;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) close(); }}>
      <DialogContent className="max-w-2xl w-[calc(100vw-2rem)] max-h-[88vh] overflow-y-auto" data-testid="dialog-import-order">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2">
            <Sparkles size={20} className="text-[var(--pop-pink)]" /> Bestellung importieren
          </DialogTitle>
        </DialogHeader>

        {!preview ? (
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-2" data-testid="import-mode-switch">
              {MODES.map((m) => (
                <button key={m.key} type="button" data-testid={`import-mode-${m.key}`}
                        onClick={() => { setMode(m.key); setFile(null); }}
                        className={`flex flex-col items-center gap-1 rounded-xl border p-3 text-xs font-bold transition-all ${mode === m.key ? "border-[var(--pop-pink)] bg-[var(--pop-pink)]/10 text-[var(--pop-pink)]" : "border-[var(--border)] text-[var(--text-muted)] hover:border-[var(--pop-pink)]"}`}>
                  <m.icon size={20} /> {m.label}
                </button>
              ))}
            </div>

            {mode === "email" ? (
              <div className="space-y-2">
                <Label>Bestelltext einfügen</Label>
                <textarea data-testid="import-email-text" value={text} onChange={(e) => setText(e.target.value)}
                          rows={8} placeholder="Text der Bestell-E-Mail hier einfügen…"
                          className="w-full rounded-xl border border-[var(--border)] bg-[var(--surface)] p-3 text-sm focus:outline-none focus:border-[var(--pop-pink)]" />
                <div className="text-xs text-[var(--text-muted)]">oder .eml-Datei hochladen:</div>
                <Input type="file" accept=".eml,message/rfc822,text/plain" data-testid="import-email-file"
                       onChange={(e) => setFile(e.target.files?.[0] || null)} />
              </div>
            ) : (
              <div className="space-y-2">
                <Label>{mode === "pdf" ? "PDF-Datei" : "Bild / Scan"}</Label>
                <Input type="file" data-testid="import-file"
                       accept={mode === "pdf" ? "application/pdf,.pdf" : "image/*"}
                       onChange={(e) => setFile(e.target.files?.[0] || null)} />
                {file && <div className="text-xs text-[var(--text-muted)] truncate">{file.name}</div>}
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={close} className="rounded-full">Abbrechen</Button>
              <Button onClick={analyze} disabled={loading} data-testid="import-analyze-btn"
                      className="rounded-full bg-[var(--pop-pink)] text-white font-bold hover:opacity-90">
                {loading ? <><Loader2 size={16} className="mr-1 animate-spin" /> Erkenne…</> : <><Sparkles size={16} className="mr-1" /> Analysieren</>}
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="space-y-4" data-testid="import-preview">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="flex items-center gap-1">Kunde *
                  {preview.customer_matched
                    ? <span className="text-green-600 inline-flex items-center gap-0.5 text-xs"><CheckCircle2 size={12} /> erkannt</span>
                    : <span className="text-amber-600 inline-flex items-center gap-0.5 text-xs" data-testid="import-customer-unknown"><AlertTriangle size={12} /> unbekannt</span>}
                </Label>
                <CustomerAutocomplete value={preview.customer_id} customers={customers} serverSearch={true}
                  onChange={(id) => setPreview((p) => ({ ...p, customer_id: id, customer_matched: true }))}
                  testId="import-customer-autocomplete" />
                {!preview.customer_matched && preview.customer_name && (
                  <div className="text-xs text-[var(--text-muted)] mt-1">Erkannt: „{preview.customer_name}"{preview.customer_email ? ` · ${preview.customer_email}` : ""}</div>
                )}
              </div>
              <div>
                <Label>Wunschlieferung</Label>
                <Input type="date" data-testid="import-expected-delivery" value={preview.expected_delivery || ""}
                       onChange={(e) => setPreview((p) => ({ ...p, expected_delivery: e.target.value }))} />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-1">
                <Label>Positionen</Label>
                {unmatchedCount > 0 && (
                  <span className="text-xs text-amber-600 inline-flex items-center gap-1" data-testid="import-unmatched-note">
                    <AlertTriangle size={12} /> {unmatchedCount} nicht zugeordnet – werden ausgelassen
                  </span>
                )}
              </div>
              <div className="space-y-2">
                {preview.lines.map((l, idx) => (
                  <div key={idx} className="rounded-xl border border-[var(--border)] p-2.5 space-y-2" data-testid={`import-line-${idx}`}>
                    <div className="flex items-center justify-between gap-2">
                      <div className="text-xs text-[var(--text-muted)] truncate">Erkannt: „{l.description || "—"}"{l.sku ? ` · SKU ${l.sku}` : ""}</div>
                      {l.item_id
                        ? <span className="text-green-600 inline-flex items-center gap-0.5 text-xs shrink-0"><CheckCircle2 size={12} /> zugeordnet</span>
                        : <span className="text-amber-600 inline-flex items-center gap-0.5 text-xs shrink-0" data-testid={`import-line-unknown-${idx}`}><AlertTriangle size={12} /> unbekannt</span>}
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-[1fr_90px_100px_36px] gap-2 items-center">
                      <ItemAutocomplete value={l.item_id || ""} items={items}
                        onChange={(id, it) => setLine(idx, { item_id: id, item_name: it?.name, item_sku: it?.sku })}
                        testId={`import-item-autocomplete-${idx}`} />
                      <Input type="number" step="any" placeholder="Menge" data-testid={`import-qty-${idx}`}
                             value={l.quantity} onChange={(e) => setLine(idx, { quantity: e.target.value })} />
                      <Input type="number" step="any" placeholder="Preis €" data-testid={`import-price-${idx}`}
                             value={l.price} onChange={(e) => setLine(idx, { price: e.target.value })} />
                      <button type="button" onClick={() => removeLine(idx)} className="text-red-500 justify-self-end" data-testid={`import-remove-line-${idx}`}>
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                ))}
                {preview.lines.length === 0 && <div className="text-sm text-[var(--text-muted)] text-center py-4">Keine Positionen erkannt.</div>}
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setPreview(null)} className="rounded-full" data-testid="import-back-btn">Zurück</Button>
              <Button onClick={save} disabled={saving} data-testid="import-save-btn"
                      className="rounded-full bg-[var(--pop-yellow)] text-black hover:bg-[var(--pop-yellow-dark)] font-bold">
                {saving ? <><Loader2 size={16} className="mr-1 animate-spin" /> Speichere…</> : "Als Bestellung speichern"}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
